import { useEffect, useMemo, useRef, useState } from "react";
import { Ic } from "./icons";
import { useNav, type PageId } from "../context/NavContext";
import type { FicheDoc } from "../lib/fiches";
import type { FolderDoc } from "../lib/folders";
import { FOLDER_SWATCH } from "./CreateFolderDialog";

// Command-palette style quick switcher. Opens on Cmd/Ctrl+K (wired in
// AppShell), lists fiches / folders / primary pages, filters on substring
// match (case-insensitive, scored by match position + length). Arrow keys
// navigate, Enter opens, Esc closes. No external library — fuzzy matching is
// simple enough that the ~30 lines of scoring here beat the weight of fuse.js.

type ItemKind = "fiche" | "folder" | "page";

type Item = {
  kind: ItemKind;
  id: string;
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  action: () => void;
};

export const QuickSwitcher = ({
  open,
  onClose,
  fiches,
  folders,
}: {
  open: boolean;
  onClose: () => void;
  fiches: FicheDoc[];
  folders: FolderDoc[];
}) => {
  const { go } = useNav();
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state every time the dialog opens so the previous query doesn't
  // flash in before the user starts typing.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCursor(0);
    // Focus the input on the next tick — input is freshly mounted.
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  const pages: Item[] = useMemo(
    () => [
      {
        kind: "page",
        id: "dashboard",
        label: "Dashboard",
        icon: <Ic.Grid size={14} className="text-ink-500" />,
        action: () => go("dashboard"),
      },
      {
        kind: "page",
        id: "fiches",
        label: "Mes fiches",
        icon: <Ic.File size={14} className="text-ink-500" />,
        action: () => go("fiches"),
      },
      {
        kind: "page",
        id: "nouvelle",
        label: "Nouvelle analyse",
        icon: <Ic.Plus size={14} className="text-ink-500" />,
        action: () => go("nouvelle"),
      },
      {
        kind: "page",
        id: "settings",
        label: "Paramètres",
        icon: <Ic.Settings size={14} className="text-ink-500" />,
        action: () => go("settings" as PageId),
      },
    ],
    [go],
  );

  const ficheItems: Item[] = useMemo(
    () =>
      fiches
        .filter((f) => !f.archived)
        .map((f) => ({
          kind: "fiche" as const,
          id: f.id,
          label: f.title,
          sublabel: [f.geocode?.city, f.geocode?.postcode]
            .filter(Boolean)
            .join(" · ") || f.id,
          icon: <Ic.File size={14} className="text-ink-500" />,
          action: () => go("fiche", { ficheId: f.id }),
        })),
    [fiches, go],
  );

  const folderItems: Item[] = useMemo(
    () =>
      folders.map((fo) => ({
        kind: "folder" as const,
        id: fo.id,
        label: fo.name,
        sublabel: fo.icon ?? undefined,
        icon: (
          <span className={`w-2 h-2 rounded-full ${FOLDER_SWATCH[fo.color]}`} />
        ),
        action: () => {
          // Future: deep-link to "Mes fiches" filtered by this folder. For
          // now just land on the fiches page — the user can then use the
          // folder sidebar to drill in.
          go("fiches");
        },
      })),
    [folders, go],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [...ficheItems, ...folderItems, ...pages];
    if (!q) {
      // Without a query, surface pages first then recent-ish fiches (up to 10).
      return [...pages, ...ficheItems.slice(0, 10), ...folderItems.slice(0, 10)];
    }
    type Scored = { item: Item; score: number };
    const scored: Scored[] = [];
    for (const item of all) {
      const hay = `${item.label} ${item.sublabel ?? ""}`.toLowerCase();
      const idx = hay.indexOf(q);
      if (idx < 0) continue;
      // Simpler scoring: prefer earlier matches + shorter labels.
      const score = idx * 10 + hay.length;
      scored.push({ item, score });
    }
    return scored
      .sort((a, b) => a.score - b.score)
      .map((s) => s.item)
      .slice(0, 25);
  }, [query, ficheItems, folderItems, pages]);

  // Re-clamp cursor when the result list shrinks below the current index.
  useEffect(() => {
    if (cursor >= results.length) setCursor(Math.max(0, results.length - 1));
  }, [results, cursor]);

  function pick(item: Item) {
    item.action();
    onClose();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(results.length - 1, c + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = results[cursor];
      if (it) pick(it);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink-900/40 backdrop-blur-sm grid place-items-start pt-[12vh] p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[560px] bg-paper rounded-xl2 shadow-pop hairline overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="flex items-center gap-3 px-4 h-12 border-b border-ink-200/70">
          <Ic.Search size={16} className="text-ink-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            placeholder="Rechercher une fiche, un dossier, une page…"
            className="flex-1 bg-transparent outline-none text-[14px]"
          />
          <kbd className="text-[10px] font-mono text-ink-500 bg-ink-100 px-1.5 py-0.5 rounded">
            Esc
          </kbd>
        </div>
        <ul className="max-h-[50vh] overflow-y-auto py-1">
          {results.length === 0 && (
            <li className="px-4 py-8 text-center text-[12.5px] text-ink-500">
              Aucun résultat.
            </li>
          )}
          {results.map((item, i) => {
            const isActive = i === cursor;
            return (
              <li key={`${item.kind}:${item.id}`}>
                <button
                  onClick={() => pick(item)}
                  onMouseEnter={() => setCursor(i)}
                  className={`w-full flex items-center gap-3 px-4 h-10 text-left transition ${
                    isActive ? "bg-lime-100" : "hover:bg-ink-50"
                  }`}
                >
                  <span className="shrink-0">{item.icon}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] text-ink-900 truncate">
                      {item.label}
                    </span>
                    {item.sublabel && (
                      <span className="block text-[11px] text-ink-500 truncate">
                        {item.sublabel}
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-ink-400">
                    {kindLabel(item.kind)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <div className="px-4 h-9 border-t border-ink-200/70 flex items-center gap-3 text-[11px] text-ink-500">
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-ink-100 px-1.5 py-0.5 rounded">↑</kbd>
            <kbd className="font-mono bg-ink-100 px-1.5 py-0.5 rounded">↓</kbd>
            naviguer
          </span>
          <span className="flex items-center gap-1">
            <kbd className="font-mono bg-ink-100 px-1.5 py-0.5 rounded">⏎</kbd>
            ouvrir
          </span>
          <span className="ml-auto">
            <kbd className="font-mono bg-ink-100 px-1.5 py-0.5 rounded">⌘K</kbd>
          </span>
        </div>
      </div>
    </div>
  );
};

function kindLabel(k: ItemKind): string {
  if (k === "fiche") return "Fiche";
  if (k === "folder") return "Dossier";
  return "Page";
}
